import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as S from "./stylesUserProfile";
import { FiArrowLeft, FiPackage, FiClock, FiMapPin, FiCreditCard, FiChevronDown, FiChevronUp, FiEdit2, FiCheck, FiX } from "react-icons/fi";
import userIcon from "../../assets/img/user-icon.png";
import { getPedidosByPhone, updateContactName } from "../../services/api";
import Loading from "react-loading";
import { formatPriceWithSymbol } from "../../utils/currency";
import { getCountryCodeDigits } from "../../utils/phone";
import i18nInstance from "../../i18n";

// Formatar data curta (usa locale do i18n)
const formatDateShort = (dateString, language) => {
  if (!dateString) return '-';
  const locale = language === 'en-US' ? 'en-US' : 'pt-BR';
  return new Date(dateString).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

// Formatar hora (usa locale do i18n)
const formatTime = (dateString, language) => {
  if (!dateString) return '-';
  const locale = language === 'en-US' ? 'en-US' : 'pt-BR';
  return new Date(dateString).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Contar quantidade total de itens
const countTotalItems = (itens) => {
  if (!itens) return 0;
  if (Array.isArray(itens)) {
    return itens.reduce((acc, item) => acc + (item.qtd || item.quantidade || item.quantity || 1), 0);
  }
  return Object.keys(itens).length;
};

// Extrair lista de itens para exibição
const getItemsList = (itens) => {
  if (!itens) return [];

  const itensArray = Array.isArray(itens) ? itens : Object.values(itens);

  return itensArray.map(item => {
    const nome = item.nomeItem || item.title || item.nome || item.name || 'Item';
    const qtd = item.quantidade || item.qtd || item.quantity || 1;
    const precoBase = item.valor || item.price || item.preco || item.unit_price || 0;

    let adicionais = [];

    // Formato 1: grupo_adicionais (WhatsApp Bot)
    if (item.grupo_adicionais && Array.isArray(item.grupo_adicionais)) {
      item.grupo_adicionais.forEach(grupo => {
        if (grupo.adicionais && Array.isArray(grupo.adicionais)) {
          grupo.adicionais.forEach(ad => {
            adicionais.push({
              nome: ad.title || ad.nome || ad.name || i18nInstance.t('whatsapp:orderHistory.addon'),
              preco: ad.price || ad.preco || 0,
              qtd: ad.quantity || ad.qtd || 1
            });
          });
        }
      });
    }

    // Formato 2: grupos_adicionais_selecionados (Cardápio/Carrinho)
    if (item.grupos_adicionais_selecionados && Array.isArray(item.grupos_adicionais_selecionados)) {
      item.grupos_adicionais_selecionados.forEach(grupo => {
        if (grupo.adicionais && Array.isArray(grupo.adicionais)) {
          grupo.adicionais.forEach(ad => {
            adicionais.push({
              nome: ad.title || ad.nome || i18nInstance.t('whatsapp:orderHistory.addon'),
              preco: ad.price || ad.preco || 0,
              qtd: ad.quantity || ad.qtd || 1
            });
          });
        }
      });
    }

    // Formato 3: adicionais direto no item (PDV/legado)
    if (item.adicionais && Array.isArray(item.adicionais)) {
      item.adicionais.forEach(ad => {
        if (typeof ad === 'object') {
          adicionais.push({
            nome: ad.title || ad.nome || ad.name || i18nInstance.t('whatsapp:orderHistory.addon'),
            preco: ad.price || ad.preco || 0,
            qtd: ad.quantity || ad.qtd || 1
          });
        }
      });
    }

    // Formato 4: selectedAdicionais (outro formato possível)
    if (item.selectedAdicionais && Array.isArray(item.selectedAdicionais)) {
      item.selectedAdicionais.forEach(ad => {
        adicionais.push({
          nome: ad.title || ad.nome || i18nInstance.t('whatsapp:orderHistory.addon'),
          preco: ad.price || ad.preco || 0,
          qtd: ad.quantity || ad.qtd || 1
        });
      });
    }

    const precoAdicionais = adicionais.reduce((acc, ad) => acc + ((ad.preco || 0) * (ad.qtd || 1)), 0);
    const precoTotal = (precoBase + precoAdicionais) * qtd;
    const obs = item.obs_item || item.observacao || item.notes || null;

    return {
      nome,
      qtd,
      precoBase,
      precoTotal,
      adicionais,
      obs
    };
  });
};

// Componente de Card do Pedido Expandível
const PedidoCard = ({ pedido, t, language }) => {
  const [expanded, setExpanded] = useState(false);

  const totalItens = countTotalItems(pedido.itens);
  const itensList = getItemsList(pedido.itens);

  // Traduzir tipo de entrega
  const getDeliveryTypeLabel = (tipo) => {
    if (!tipo) return t('whatsapp:deliveryType.unknown');
    const tipoLower = tipo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (tipoLower.includes('retirada') || tipoLower.includes('pickup') || tipoLower.includes('balcao')) {
      return t('whatsapp:deliveryType.pickup');
    }
    if (tipoLower.includes('entrega') || tipoLower.includes('delivery')) {
      return t('whatsapp:deliveryType.delivery');
    }
    if (tipoLower.includes('local') || tipoLower.includes('consumo') || tipoLower.includes('dine')) {
      return t('whatsapp:deliveryType.dineIn');
    }
    if (tipoLower.includes('viagem') || tipoLower.includes('takeaway')) {
      return t('whatsapp:deliveryType.takeaway');
    }
    return tipo; // Retorna original se não encontrar match
  };

  // Traduzir tipo de pagamento
  const getPaymentTypeLabel = (tipo) => {
    if (!tipo) return t('whatsapp:paymentType.unknown');
    const tipoLower = tipo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (tipoLower.includes('dinheiro') || tipoLower.includes('cash') || tipoLower.includes('especie')) {
      return t('whatsapp:paymentType.cash');
    }
    if (tipoLower.includes('credito') || tipoLower.includes('credit')) {
      return t('whatsapp:paymentType.credit');
    }
    if (tipoLower.includes('debito') || tipoLower.includes('debit')) {
      return t('whatsapp:paymentType.debit');
    }
    if (tipoLower.includes('cartao') || tipoLower.includes('card')) {
      return t('whatsapp:paymentType.card');
    }
    if (tipoLower.includes('pix')) {
      return t('whatsapp:paymentType.pix');
    }
    if (tipoLower.includes('vale') || tipoLower.includes('voucher') || tipoLower.includes('vr') || tipoLower.includes('va')) {
      return t('whatsapp:paymentType.voucher');
    }
    if (tipoLower.includes('online') || tipoLower.includes('app')) {
      return t('whatsapp:paymentType.online');
    }
    return tipo; // Retorna original se não encontrar match
  };

  const tipoEntrega = getDeliveryTypeLabel(pedido.entrega?.tipo_entrega);
  const tipoPagamento = getPaymentTypeLabel(pedido.tipo_pagamento);

  const getStatusLabel = (status) => {
    const statusMap = {
      '0': t('whatsapp:orderStatus.pending'),
      '1': t('whatsapp:orderStatus.analysis'),
      '2': t('whatsapp:orderStatus.production'),
      '3': t('whatsapp:orderStatus.ready'),
      '4': t('whatsapp:orderStatus.finished')
    };
    return statusMap[status] || t('whatsapp:orderStatus.unknown');
  };

  return (
    <S.PedidoCard>
      <S.PedidoCardHeader onClick={() => setExpanded(!expanded)}>
        <div className="pedido-main">
          <div className="pedido-numero-status">
            <span className="numero">#{pedido.id_pedido_counter || pedido.id_pedido}</span>
            <S.StatusBadge status={pedido.status_pedido}>
              {getStatusLabel(pedido.status_pedido)}
            </S.StatusBadge>
          </div>
          <div className="pedido-data">
            <FiClock size={12} />
            <span>
              {formatDateShort(pedido.createdAt, language)} {t('whatsapp:orderHistory.at')} {formatTime(pedido.createdAt, language)}
            </span>
          </div>
        </div>

        <div className="pedido-resumo">
          <div className="info-row">
            <span className="info-item">
              <FiPackage size={12} />
              {totalItens} {totalItens === 1 ? t('whatsapp:orderHistory.item') : t('whatsapp:orderHistory.items')}
            </span>
            <span className="info-item">
              <FiMapPin size={12} />
              {tipoEntrega}
            </span>
            <span className="info-item">
              <FiCreditCard size={12} />
              {tipoPagamento}
            </span>
          </div>
          <div className="valor-expand">
            <span className="valor">{formatPriceWithSymbol(pedido.valor_total || 0)}</span>
            {expanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </div>
        </div>
      </S.PedidoCardHeader>

      {expanded && itensList.length > 0 && (
        <S.PedidoCardDetails>
          <div className="itens-title">{t('whatsapp:orderHistory.orderItems')}</div>
          <div className="itens-lista">
            {itensList.map((item, index) => (
              <div key={index} className="item-container">
                <div className="item-row">
                  <span className="item-qtd">{item.qtd}x</span>
                  <span className="item-nome">{item.nome}</span>
                  <span className="item-preco">
                    {formatPriceWithSymbol(item.precoTotal)}
                  </span>
                </div>

                {item.adicionais && item.adicionais.length > 0 && (
                  <div className="adicionais-lista">
                    {item.adicionais.map((ad, adIndex) => (
                      <div key={adIndex} className="adicional-row">
                        <span className="adicional-bullet">+</span>
                        <span className="adicional-nome">
                          {ad.qtd > 1 ? `${ad.qtd}x ` : ''}{ad.nome}
                        </span>
                        {ad.preco > 0 && (
                          <span className="adicional-preco">
                            {formatPriceWithSymbol(ad.preco * ad.qtd)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {item.obs && (
                  <div className="item-obs">
                    <span>{t('whatsapp:orderHistory.observation')}: {item.obs}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {pedido.entrega?.valor > 0 && (
            <div className="item-row entrega">
              <span className="item-qtd">1x</span>
              <span className="item-nome">{t('whatsapp:orderHistory.deliveryFee')}</span>
              <span className="item-preco">
                {formatPriceWithSymbol(pedido.entrega.valor)}
              </span>
            </div>
          )}

          {pedido.desconto > 0 && (
            <div className="item-row desconto">
              <span className="item-qtd">-</span>
              <span className="item-nome">{t('whatsapp:orderHistory.discount')}</span>
              <span className="item-preco desconto-valor">
                - {formatPriceWithSymbol(pedido.desconto)}
              </span>
            </div>
          )}

          {pedido.descricao && (
            <div className="obs">
              <strong>{t('whatsapp:orderHistory.observation')}:</strong> {pedido.descricao}
            </div>
          )}
        </S.PedidoCardDetails>
      )}
    </S.PedidoCard>
  );
};

const UserProfile = ({ isOpen, onClose, user, empresaID, onNameUpdate }) => {
  const { t, i18n } = useTranslation(['whatsapp']);
  const [pedidos, setPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);

  // Estados para edição do nome
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Formatar número de telefone
  const formatPhoneNumber = (number) => {
    if (!number) return t('whatsapp:contactData.numberPlaceholder');

    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length < 10) return number;

    const countryCode = cleaned.substring(0, 2);
    const areaCode = cleaned.substring(2, 4);
    const hasNineDigits = cleaned.length >= 12;
    const firstPart = cleaned.substring(4, hasNineDigits ? 5 : 4);
    const middlePart = cleaned.substring(hasNineDigits ? 5 : 4, cleaned.length - 4);
    const lastPart = cleaned.substring(cleaned.length - 4);

    return `+${countryCode} ${areaCode} ${firstPart} ${middlePart}-${lastPart}`;
  };

  // Iniciar edição do nome
  const handleStartEdit = () => {
    setEditedName(user?.name || '');
    setIsEditingName(true);
  };

  // Cancelar edição
  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  // Salvar nome editado
  const handleSaveName = async () => {
    if (!editedName.trim() || !user?._id || !empresaID) return;

    setSavingName(true);
    try {
      const response = await updateContactName(empresaID, user._id, editedName.trim());

      if (response.data?.status === 200) {
        toast.success(t('whatsapp:contactData.nameUpdated'));
        setIsEditingName(false);

        // Notificar o componente pai para atualizar a lista de chats
        if (onNameUpdate) {
          onNameUpdate(user._id, editedName.trim());
        }
      }
    } catch (error) {
      console.error('[UserProfile] Erro ao atualizar nome:', error);
      toast.error(t('whatsapp:contactData.nameUpdateError'));
    } finally {
      setSavingName(false);
    }
  };

  // Buscar pedidos do cliente
  const fetchPedidosDoCliente = useCallback(async (telefone) => {
    if (!telefone) return;

    setLoadingPedidos(true);
    try {
      let telefoneLimpo = telefone.replace(/\D/g, "");

      const countryCodeDigits = getCountryCodeDigits();
      if (telefoneLimpo.startsWith(countryCodeDigits)) {
        telefoneLimpo = telefoneLimpo.substring(countryCodeDigits.length);
      }

      const ultimos8Digitos = telefoneLimpo.slice(-8);
      const regexFlexivel = ultimos8Digitos.split('').join('[^0-9]*');

      console.log('[UserProfile] Buscando pedidos com regex flexível:', regexFlexivel);
      const response = await getPedidosByPhone(regexFlexivel, 100);
      setPedidos(response.data?.pedidos || []);
    } catch (error) {
      console.error('[UserProfile] Erro ao buscar pedidos:', error);
      setPedidos([]);
    } finally {
      setLoadingPedidos(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && user?.mobile_number) {
      fetchPedidosDoCliente(user.mobile_number);
    } else if (!isOpen) {
      setPedidos([]);
      setIsEditingName(false);
    }
  }, [isOpen, user?.mobile_number, fetchPedidosDoCliente]);

  if (!isOpen) return null;

  const formattedNumber = formatPhoneNumber(user?.mobile_number);
  const totalPedidos = pedidos.length;
  const totalGasto = pedidos.reduce((acc, p) => acc + (p.valor_total || 0), 0);

  return (
    <S.Wrapper>
      <S.Header>
        <S.Back>
          <FiArrowLeft onClick={onClose} />
          <h3>{t('whatsapp:contactData.title')}</h3>
        </S.Back>
      </S.Header>

      <S.ContentScroll>
        <S.UserImage>
          <img
            src={user?.channel_data?.whatsapp?.profile_picture || userIcon}
            alt={t('whatsapp:contactData.profilePhoto')}
            onError={(e) => { e.target.src = userIcon; }}
          />
        </S.UserImage>

        <S.UserData>
          <span>{t('whatsapp:contactData.name')}</span>
          {isEditingName ? (
            <S.EditNameContainer>
              <S.NameInput
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder={t('whatsapp:contactData.enterName')}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
              />
              <S.EditActions>
                <S.EditButton
                  onClick={handleSaveName}
                  disabled={savingName || !editedName.trim()}
                  $variant="save"
                  title={t('whatsapp:contactData.saveName')}
                >
                  {savingName ? <Loading type="spin" color="#fff" height={14} width={14} /> : <FiCheck size={16} />}
                </S.EditButton>
                <S.EditButton
                  onClick={handleCancelEdit}
                  disabled={savingName}
                  $variant="cancel"
                  title={t('whatsapp:contactData.cancelEdit')}
                >
                  <FiX size={16} />
                </S.EditButton>
              </S.EditActions>
            </S.EditNameContainer>
          ) : (
            <S.NameDisplay>
              <p>{user?.name || t('whatsapp:contactData.namePlaceholder')}</p>
              <S.EditIconButton
                onClick={handleStartEdit}
                title={t('whatsapp:contactData.editName')}
              >
                <FiEdit2 size={14} />
              </S.EditIconButton>
            </S.NameDisplay>
          )}
        </S.UserData>

        <S.UserData>
          <span>{t('whatsapp:contactData.number')}</span>
          <p>{formattedNumber}</p>
        </S.UserData>

        {totalPedidos > 0 && (
          <S.StatsContainer>
            <div className="stat-item">
              <span className="stat-value">{totalPedidos}</span>
              <span className="stat-label">
                {totalPedidos === 1 ? t('whatsapp:orderHistory.order') : t('whatsapp:orderHistory.orders')}
              </span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-value">{formatPriceWithSymbol(totalGasto)}</span>
              <span className="stat-label">{t('whatsapp:orderHistory.totalSpent')}</span>
            </div>
          </S.StatsContainer>
        )}

        <S.UserData>
          <span>{t('whatsapp:orderHistory.title')}</span>
          {loadingPedidos ? (
            <S.LoadingContainer>
              <Loading type="spin" color="#008069" height={20} width={20} />
            </S.LoadingContainer>
          ) : pedidos.length === 0 ? (
            <S.EmptyMessage>{t('whatsapp:orderHistory.empty')}</S.EmptyMessage>
          ) : (
            <S.PedidosList>
              {pedidos.map(pedido => (
                <PedidoCard
                  key={pedido._id}
                  pedido={pedido}
                  t={t}
                  language={i18n.language}
                />
              ))}
            </S.PedidosList>
          )}
        </S.UserData>
      </S.ContentScroll>
    </S.Wrapper>
  );
};

export default UserProfile;
